import { useMutation, useQuery } from "@apollo/client";
import type {
  CreateSalonTotalCapacityInput,
  SalonTotalCapacityFilterInput,
  UpdateSalonTotalCapacityInput,
} from "@/gql/graphql";
import {
  CREATE_SALON_TOTAL_CAPACITY,
  DELETE_SALON_TOTAL_CAPACITY,
  GET_SALON_TOTAL_CAPACITIES,
  GET_SALON_TOTAL_CAPACITY,
  UPDATE_SALON_TOTAL_CAPACITY,
} from "@/graphql/salon-total-capacity";

/**
 * Hook to fetch all salon total capacities
 */
export function useSalonTotalCapacities(salonId: string, filters?: SalonTotalCapacityFilterInput) {
  return useQuery(GET_SALON_TOTAL_CAPACITIES, {
    variables: { salonId, filters },
    skip: !salonId,
  });
}

/**
 * Hook to fetch a single salon total capacity
 */
export function useSalonTotalCapacity(salonId: string, capacityId?: string) {
  return useQuery(GET_SALON_TOTAL_CAPACITY, {
    variables: { salonId, capacityId: capacityId ?? "" },
    skip: !salonId || !capacityId,
  });
}

/**
 * Hook to create a salon total capacity
 */
export function useCreateSalonTotalCapacity() {
  const [mutate, result] = useMutation(CREATE_SALON_TOTAL_CAPACITY, {
    refetchQueries: [GET_SALON_TOTAL_CAPACITIES],
  });

  return {
    execute: async (variables: { salonId: string; input: CreateSalonTotalCapacityInput }) => {
      const response = await mutate({ variables });
      return response.data?.createSalonTotalCapacity;
    },
    ...result,
  };
}

/**
 * Hook to update a salon total capacity
 */
export function useUpdateSalonTotalCapacity() {
  const [mutate, result] = useMutation(UPDATE_SALON_TOTAL_CAPACITY, {
    refetchQueries: [GET_SALON_TOTAL_CAPACITIES],
  });

  return {
    execute: async (variables: {
      salonId: string;
      capacityId: string;
      input: UpdateSalonTotalCapacityInput;
    }) => {
      const response = await mutate({ variables });
      return response.data?.updateSalonTotalCapacity;
    },
    ...result,
  };
}

/**
 * Hook to delete a salon total capacity
 */
export function useDeleteSalonTotalCapacity() {
  const [mutate, result] = useMutation(DELETE_SALON_TOTAL_CAPACITY, {
    refetchQueries: [GET_SALON_TOTAL_CAPACITIES],
  });

  return {
    execute: async (variables: { salonId: string; capacityId: string }) => {
      const response = await mutate({ variables });
      return response.data?.deleteSalonTotalCapacity;
    },
    ...result,
  };
}
